import { computed, reactive, watch } from 'vue'
import { useAuthState } from '~/composables/useAuthState'
import { getApiClient } from '~/utils/api'
import { createReadRequester } from './api'
import { createIncentivesController, initialState } from './state'

// The Nuxt application owns this controller; no module-global account data crosses SSR requests.
const controllers = new WeakMap<object, ReturnType<typeof createIncentivesController>>()
/** Opt-in foundation. Mounting this composable never reads or writes rewards automatically. */
export function useIncentives() {
  const app = useNuxtApp()
  const { user, isAuthenticated, isAuthReady, fetchAndStoreToken } = useAuthState()
  const { session } = useSession()
  const { locale } = useI18n()
  const identity = computed(() => isAuthReady.value && isAuthenticated.value && user.value?.id && session.value?.id ? `${user.value.id}:${session.value.id}` : '')
  let controller = controllers.get(app)
  if (!controller) {
    const state = reactive(initialState())
    const request = createReadRequester({
      transport: getApiClient(), token: fetchAndStoreToken,
      current: context => context.identity === identity.value && context.identity === state.identity && context.generation === state.generation,
    })
    controller = createIncentivesController(state, request)
    controllers.set(app, controller)
  }
  const shared = controller
  watch([identity, locale], ([owner, lang]) => shared.setSession(owner, lang), { immediate: true, flush: 'sync' })
  return { identity, state: shared.state, load: shared.load, clear: shared.clear, cancel: shared.cancel, invalidate: shared.invalidate }
}
